import { useDraggableDrawer } from "@dash/nav";
import { AppLinkTree } from "@megarax/links";
import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";

import { mobileNavCollapsedBarHeightPx, mobileNavHandleColor } from "./navConstants";
import { getMobileDrawerSheetHeight } from "./navHelpers";
import { MobileNavCollapsedBar } from "./MobileNavCollapsedBar";
import { MobileNavDrawerSheet } from "./MobileNavDrawerSheet";
import { toMobileNavShortcuts } from "./mobileAppShortcuts";

type Props = {
  linkTree: AppLinkTree[];
  onSignOut: () => void;
  showProductionEnvAlert?: boolean;
};

export const MegaraxNavDrawer: React.FC<Props> = ({ linkTree, onSignOut, showProductionEnvAlert }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navElement = useRef<HTMLDivElement>(null);
  const collapsedBarRef = useRef<HTMLDivElement>(null);
  const [sheetHeight, setSheetHeight] = useState(getMobileDrawerSheetHeight);
  const drawer = useDraggableDrawer({ baseHeight: sheetHeight, navElement });
  const shortcuts = useMemo(
    () => toMobileNavShortcuts(linkTree, location.pathname, searchParams),
    [linkTree, location.pathname, searchParams],
  );

  const dragHandleCss = {
    display: "flex",
    justifyContent: "center",
    paddingTop: "0.3125rem",
    paddingBottom: "0.3125rem",
    flexShrink: 0,
    touchAction: "none",
  };

  const dragHandleBarCss = {
    width: "3.625rem",
    height: "0.1875rem",
    borderRadius: "0.25rem",
    background: mobileNavHandleColor,
  };

  useEffect(() => {
    const updateSheetHeight = () => {
      const collapsedBarHeight =
        collapsedBarRef.current?.offsetHeight ?? mobileNavCollapsedBarHeightPx;
      setSheetHeight(getMobileDrawerSheetHeight(collapsedBarHeight));
    };

    updateSheetHeight();
    window.visualViewport?.addEventListener("resize", updateSheetHeight);
    window.addEventListener("resize", updateSheetHeight);

    return () => {
      window.visualViewport?.removeEventListener("resize", updateSheetHeight);
      window.removeEventListener("resize", updateSheetHeight);
    };
  }, []);

  useEffect(() => {
    drawer.setIsExpanded(false);
  }, [location.pathname]);

  const isOpen = drawer.isExpanded || drawer.isDragging;

  return (
    <div
      css={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        zIndex: 1009,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {isOpen && (
        <motion.div
          onClick={() => drawer.setIsExpanded(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.35 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          css={{ position: "fixed", inset: 0, backgroundColor: "black", zIndex: 1 }}
        />
      )}

      <div ref={navElement} css={{ position: "relative", zIndex: 2 }}>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            maxHeight: isOpen ? drawer.drawerHeight : "0px",
            height: isOpen ? drawer.drawerHeight : "0px",
            overflow: "hidden",
            transition: drawer.isDragging ? undefined : "max-height 0.15s linear, height 0.15s linear",
            background: "white",
            boxShadow: drawer.isExpanded ? "0 -8px 32px rgba(0,0,0,0.12)" : "none",
          }}
        >
          {isOpen && (
            <div onTouchStart={drawer.onTouchStart} css={dragHandleCss}>
              <div css={dragHandleBarCss} />
            </div>
          )}
          <MobileNavDrawerSheet
            linkTree={linkTree}
            onSignOut={onSignOut}
            showProductionEnvAlert={showProductionEnvAlert}
            onNavigate={() => drawer.setIsExpanded(false)}
          />
        </div>

        {!isOpen && (
          <div onTouchStart={drawer.onTouchStart} css={{ ...dragHandleCss, background: "transparent" }}>
            <div css={dragHandleBarCss} />
          </div>
        )}

        <MobileNavCollapsedBar
          barRef={collapsedBarRef}
          shortcuts={shortcuts}
          onMenuClick={() => drawer.setIsExpanded(!drawer.isExpanded)}
        />
      </div>
    </div>
  );
};
